import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import useAdminAuth from '../hooks/useAdminAuth';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ConfirmModal from '../components/ConfirmModal';
import { Save, Edit2, Plus, Trash2, Image as ImageIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CMSAdmin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { getAdminEmail } = useAdminAuth();
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const adminEmail = getAdminEmail();
  const [selectedSection, setSelectedSection] = useState('all');

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/cms/content-list`);
      // Ensure response.data is an array
      const contentData = Array.isArray(response.data) ? response.data : [];
      setContent(contentData);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load content',
        variant: 'destructive'
      });
      setContent([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (item) => {
    try {
      await axios.put(`${API}/cms/content/${item.section}/${item.key}`, {
        value: item.value,
        updated_by: adminEmail
      });
      
      toast({
        title: 'Success',
        description: 'Content updated successfully'
      });
      
      setEditingItem(null);
      fetchContent();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update content',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    
    setLoading(true);
    try {
      await axios.delete(`${API}/cms/content/${itemToDelete.section}/${itemToDelete.key}`);
      
      toast({
        title: 'Success',
        description: 'Content deleted successfully'
      });
      
      setShowDeleteModal(false);
      setItemToDelete(null);
      fetchContent();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete content',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    const newItem = {
      section: 'new_section',
      key: 'new_key',
      content_type: 'text',
      value: 'New content'
    };
    
    try {
      await axios.post(`${API}/cms/content`, newItem);
      toast({
        title: 'Success',
        description: 'New content item created'
      });
      fetchContent();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create content',
        variant: 'destructive'
      });
    }
  };

  const sections = [...new Set(content.map(item => item.section))];
  const filteredContent = selectedSection === 'all' 
    ? content 
    : content.filter(item => item.section === selectedSection);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-12 text-center">
          <p>Loading...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="container mx-auto px-4 sm:px-6 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-bold text-[#1e3a5f]">CMS Content Management</h1>
            <Button
              onClick={handleCreate}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add New Content
            </Button>
          </div>

          {/* Admin Email Display */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <Label>Logged in as:</Label>
            <p className="text-sm text-gray-600 mt-1">{adminEmail}</p>
          </div>

          {/* Section Filter */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <Label>Filter by Section</Label>
            <div className="flex flex-wrap gap-2 mt-3">
              <Button
                variant={selectedSection === 'all' ? 'default' : 'outline'}
                onClick={() => setSelectedSection('all')}
                className="text-sm"
              >
                All Sections
              </Button>
              {sections.map(section => (
                <Button
                  key={section}
                  variant={selectedSection === section ? 'default' : 'outline'}
                  onClick={() => setSelectedSection(section)}
                  className="text-sm"
                >
                  {section}
                </Button>
              ))}
            </div>
          </div>

          {/* Content Items */}
          <div className="space-y-4">
            {filteredContent.map((item) => (
              <div key={item.id} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                        {item.section}
                      </span>
                      <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-semibold">
                        {item.key}
                      </span>
                      <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-semibold">
                        {item.content_type}
                      </span>
                    </div>
                    
                    {editingItem?.id === item.id ? (
                      <div className="space-y-3 mt-4">
                        {item.content_type === 'image_url' ? (
                          <div>
                            <Label>Image URL</Label>
                            <Input
                              value={editingItem.value}
                              onChange={(e) =>
                                setEditingItem({ ...editingItem, value: e.target.value })
                              }
                              placeholder="https://..."
                            />
                            {editingItem.value && (
                              <img
                                src={editingItem.value}
                                alt="Preview"
                                className="mt-2 max-w-xs rounded border"
                              />
                            )}
                          </div>
                        ) : item.content_type === 'json' ? (
                          <div>
                            <Label>JSON Content</Label>
                            <Textarea
                              value={editingItem.value}
                              onChange={(e) =>
                                setEditingItem({ ...editingItem, value: e.target.value })
                              }
                              rows={10}
                              className="font-mono text-sm"
                            />
                          </div>
                        ) : (
                          <div>
                            <Label>Content</Label>
                            <Textarea
                              value={editingItem.value}
                              onChange={(e) =>
                                setEditingItem({ ...editingItem, value: e.target.value })
                              }
                              rows={5}
                            />
                          </div>
                        )}
                        
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleSave(editingItem)}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <Save className="w-4 h-4 mr-2" />
                            Save Changes
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => setEditingItem(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-4">
                        {item.content_type === 'image_url' ? (
                          <div>
                            <img
                              src={item.value}
                              alt={item.key}
                              className="max-w-md rounded border mb-2"
                            />
                            <p className="text-sm text-gray-600 break-all">{item.value}</p>
                          </div>
                        ) : item.content_type === 'json' ? (
                          <pre className="bg-gray-50 p-3 rounded text-xs overflow-auto">
                            {JSON.stringify(JSON.parse(item.value), null, 2)}
                          </pre>
                        ) : (
                          <p className="text-gray-700 whitespace-pre-wrap">{item.value}</p>
                        )}
                        
                        {item.last_updated && (
                          <p className="text-xs text-gray-500 mt-3">
                            Last updated: {new Date(item.last_updated).toLocaleString()}
                            {item.updated_by && ` by ${item.updated_by}`}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {editingItem?.id !== item.id && (
                    <div className="flex gap-2 ml-4">
                      <Button
                        variant="outline"
                        onClick={() => setEditingItem({ ...item })}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleDelete(item)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {filteredContent.length === 0 && (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <p className="text-gray-600">No content items found in this section.</p>
              <Button
                onClick={handleCreate}
                className="mt-4 bg-green-600 hover:bg-green-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create First Content Item
              </Button>
            </div>
          )}
        </div>
      </div>
      
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setItemToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete CMS Content"
        message={itemToDelete ? `Are you sure you want to delete "${itemToDelete.section} > ${itemToDelete.key}"?\n\nThis action cannot be undone.` : ''}
        confirmText="Delete Content"
        variant="danger"
        loading={loading}
      />
      
      <Footer />
    </div>
  );
};

export default CMSAdmin;
